// =============================================================================
// Pure helpers that work on ANY resume object.
//
// src/data/resume.js holds one specific resume (the site owner's) and binds
// these to it. The resume builder feeds in whatever the visitor has typed.
// Nothing here reads module-level state, so both callers get the same maths.
// =============================================================================

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** "2023-10" -> Date. Accepts a bare "2023" too. */
function parseMonth(value) {
  const [year, month = "1"] = String(value).split("-");
  return new Date(Number(year), Number(month) - 1, 1);
}

/** "2023-10" -> "Oct 2023". An empty value means the role is current. */
export function formatMonth(value) {
  if (!value) return "Present";
  const date = parseMonth(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return `${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

/** Whole months between two dates, floored at 0. */
export function monthsBetween(from, to) {
  return Math.max(
    0,
    (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth())
  );
}

/** Completed years since `careerStart` ("YYYY-MM"). */
export function yearsOfExperience(careerStart, now = new Date()) {
  if (!careerStart) return 0;
  return Math.floor(monthsBetween(parseMonth(careerStart), now) / 12);
}

/** "Oct 2023 - Present (2 yrs 11 mos)". */
export function formatRange(start, end, now = new Date()) {
  if (!start) return "";
  const months = monthsBetween(parseMonth(start), end ? parseMonth(end) : now);
  const years = Math.floor(months / 12);
  const rest = months % 12;
  const parts = [];
  if (years) parts.push(`${years} yr${years > 1 ? "s" : ""}`);
  if (rest) parts.push(`${rest} mo${rest > 1 ? "s" : ""}`);
  const duration = parts.length ? ` (${parts.join(" ")})` : "";
  return `${formatMonth(start)} - ${formatMonth(end)}${duration}`;
}

/**
 * The summary with {years} substituted. Falls back to `summary` when a resume
 * has plain prose instead of a template - builder users type prose.
 */
export function renderSummary(profile = {}, now = new Date()) {
  const template = profile.summaryTemplate || profile.summary || "";
  if (!template.includes("{years}")) return template;
  return template.replace(
    /\{years\}/g,
    yearsOfExperience(profile.careerStart, now)
  );
}

/**
 * The earliest role start, used when no explicit careerStart is given - which
 * is the normal case for someone filling in the builder.
 */
export function inferCareerStart(experience = []) {
  const starts = experience.map((job) => job.start).filter(Boolean).sort();
  return starts[0];
}

/** Strip the scheme (and any trailing slash) for display: "github.com/x". */
export function displayUrl(url = "") {
  return url.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "");
}

/** Contact details as one plain line - the shape ATS parsers read best. */
export function contactLine(profile = {}) {
  return [profile.email, profile.phone, profile.location]
    .filter(Boolean)
    .join("  |  ");
}

/** Profile links as one plain line. */
export function linkLine(profile = {}) {
  return [profile.website, profile.linkedin, profile.github, profile.portfolio]
    .filter(Boolean)
    .map(displayUrl)
    .join("  |  ");
}
